<template>
  <q-dialog v-model="show" data-testid="note-dialog" persistent>
    <div class="note-dialog full-width">
      <c-page-section squares>
        <q-card class="full-width">
          <q-card-section>
            <div class="heading">
              {{ title }}
              <q-icon name="close" class="close-icon" @click="closeDialog" />
            </div>
          </q-card-section>
          <q-separator />
          <q-card-section>
            <div class="row q-mt-lg q-mb-md">
              <div class="col">
                <div class="q-mb-sm">
                  {{ t('collabNotes.dialog.note') }}
                </div>
                <wysiwyg-editor-v2 ref="editor" class="note" :smaller-headers="true" @ready="onEditorReady" />
              </div>
            </div>
          </q-card-section>
          <q-card-actions class="dialog-action">
            <c-dialog-form-btns
              :negative-label="t('cancelBtn')"
              :positive-label="t('submitBtn')"
              positive-data-testid="note-submit-button"
              @action:negative="closeDialog"
              @action:positive="onConfirm"
            ></c-dialog-form-btns>
          </q-card-actions>
        </q-card>
      </c-page-section>
    </div>
  </q-dialog>
  <c-confirm-dialog ref="confirmDialog" />
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CPageSection from '@/components/_common/PageSection.vue';
import CDialogFormBtns from '@/components/_common/DialogFormBtns.vue';
import { hubClient } from '@tfpl/api-client';
import WysiwygEditorV2 from '@/components/_common/WysiwygEditorV2.vue';
import { useCollabNote } from '@/composables/useCollabNote';
import CConfirmDialog from '@/components/_common/confirmDialog/ConfirmDialog.vue';

/*****************************************************************************
 * EMITS
 ****************************************************************************/
const emit = defineEmits(['saved']);

/*****************************************************************************
 * SETUP
 ****************************************************************************/

const { t } = useI18n();
const show = ref(false);
const title = ref('');
const { createNote, updateNote } = useCollabNote();
const confirmDialog = ref();

/**
 * State
 */
const defaultState = {
  isNew: true,
  note: null,
  userUuid: null,
};
const state = ref<{
  isNew: boolean;
  note: string | null;
  userUuid: string | null;
  uuid?: string | null;
}>(defaultState);

const resetState = () => {
  state.value.isNew = true;
  state.value.note = '';
  state.value.userUuid = null;
  state.value.uuid = null;
};

/**
 * Dialog open / close
 * @param userUuid
 * @param note
 */
const openDialog = (userUuid: string, note?: hubClient.CollaboratorNoteDto) => {
  resetState();
  state.value.userUuid = userUuid;
  if (note) {
    title.value = t('collabNotes.dialog.edit');
    state.value.note = note.note;
    state.value.uuid = note.uuid;
    state.value.isNew = false;
  } else {
    title.value = t('collabNotes.dialog.add');
  }
  show.value = true;
};

const closeDialog = () => {
  show.value = false;
};

const onConfirm = async () => {
  if (state.value.uuid) {
    // edit
    await updateNote(state.value.uuid, {
      note: editor.value.getHTML(),
    });
  } else {
    // create
    await createNote({
      collaboratorUuid: state.value.userUuid as string,
      note: editor.value.getHTML(),
    });
  }

  emit('saved');
  closeDialog();
};

const editor = ref();
const onEditorReady = () => {
  if (state.value.isNew) {
    editor.value.setHTML('');
  } else {
    editor.value.setHTML(state.value.note);
  }
};

/*****************************************************************************
 * EXPOSE
 ****************************************************************************/
defineExpose({
  openDialog,
});
</script>
<style>
.note {
  height: 100%;
}

.note-dialog {
  max-width: 900px !important;
}

.template-dropdown {
  min-height: 60px;
}
</style>
